import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';
import { useAuth } from '@/features/auth/hooks';
import { socketUrl } from '@/shared/config';
import { logger } from '@/shared/utils/logger';
import { useSocketStore } from '@/store/realtime/useSocketStore';

interface SocketContextValue {
  socket: Socket | null;
}

const SocketContext = createContext<SocketContextValue>({ socket: null });

interface SocketProviderProps {
  children: React.ReactNode;
}

const isE2EAuthMode = (): boolean =>
  import.meta.env.MODE === 'test' &&
  typeof window !== 'undefined' &&
  Boolean((window as { __E2E_AUTH__?: unknown }).__E2E_AUTH__);

/** Minimum interval between token refresh attempts (ms) */
const TOKEN_REFRESH_THROTTLE_MS = 10_000;

/** Auth-related error patterns that indicate a stale/expired token */
const isAuthError = (error: Error | string): boolean => {
  const msg = typeof error === 'string' ? error : error.message;
  return (
    msg.includes('jwt') ||
    msg.includes('token') ||
    msg.includes('unauthorized') ||
    msg.includes('authentication') ||
    msg.includes('403') ||
    msg.includes('401')
  );
};

/**
 * SocketProvider - Manages Socket.io instance lifecycle
 *
 * Responsibilities:
 * - Create and destroy socket instance based on auth state
 * - Handle connection/disconnection events
 * - Refresh auth token on auth-related reconnection failures
 * - Update connection state in Zustand store
 * - Provide socket instance via Context
 *
 * Authentication Requirements:
 * - Uses `userId` (Auth0 sub) for ALL user types (clients, providers, staff)
 * - Do NOT use `providerId` - it's empty for regular clients/customers
 * - Backend expects `userId` in socket.handshake.auth
 *
 * Note: Event listeners are added by feature-specific hooks, not here
 */
export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const socketRef = useRef<Socket | null>(null);
  // State to trigger re-renders when socket is created/destroyed
  const [socket, setSocket] = useState<Socket | null>(null);
  // IMPORTANT: Use `userId` not `providerId` - userId is Auth0 sub and exists for ALL user types
  // providerId is only populated for provider roles (empty for regular clients)
  const { isAuthenticated, userId, role, accessToken, getNewToken } = useAuth();
  const {
    setConnected,
    setError,
    incrementConnectionAttempts,
    setReconnecting,
    setReconnectAttempt,
    resetReconnectAttempt,
  } = useSocketStore();

  // Track last token refresh to throttle Auth0 calls during bad networks
  const lastTokenRefreshRef = useRef<number>(0);

  /**
   * Attempt to refresh the auth token and update the socket's auth.
   * Throttled to avoid spamming Auth0 during rapid reconnection attempts.
   * Returns the new token or null if refresh was skipped/failed.
   */
  const refreshSocketToken = async (): Promise<string | null> => {
    const now = Date.now();
    if (now - lastTokenRefreshRef.current < TOKEN_REFRESH_THROTTLE_MS) {
      logger.debug('SocketContext: Token refresh throttled');
      return null;
    }
    lastTokenRefreshRef.current = now;

    try {
      const newToken = await getNewToken();
      if (newToken && socketRef.current) {
        socketRef.current.auth = { token: newToken };
        logger.info('SocketContext: Socket auth token refreshed');
      }
      return newToken;
    } catch (err) {
      logger.error('SocketContext: Failed to refresh token for socket:', err);
      return null;
    }
  };

  useEffect(() => {
    if (isE2EAuthMode()) {
      logger.debug('SocketContext: E2E auth mode, skipping socket initialization');
      return;
    }

    // Only initialize if authenticated with required data and access token
    // IMPORTANT: Check `userId` not `providerId` - all users have userId, only providers have providerId
    // NOTE: `role` can be null for regular clients (customers) - they should still connect
    // NOTE: `accessToken` is required for JWT-based socket authentication
    if (!isAuthenticated || !userId || !accessToken) {
      logger.debug(
        'SocketContext: Not authenticated, skipping socket initialization',
      );
      return;
    }

    // Clean up any existing socket before creating new one
    if (socketRef.current) {
      logger.debug('SocketContext: Cleaning up existing socket');
      socketRef.current.removeAllListeners();
      socketRef.current.close();
      socketRef.current = null;
      setSocket(null);
    }

    logger.debug('SocketContext: Initializing socket', {
      userId: userId,
      role,
      socketUrl,
    });

    // Increment connection attempts counter
    incrementConnectionAttempts();

    // Create new socket instance
    // Backend verifies JWT token and extracts userId/role from verified claims
    const newSocket = io(socketUrl, {
      auth: {
        token: accessToken, // JWT token verified server-side via JWKS/RS256
      },
      // WebSocket preferred, polling as fallback
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30_000, // Cap backoff at 30 seconds
      randomizationFactor: 0.3, // Jitter to prevent thundering herd
      timeout: 20000,
    });

    // Set up connection lifecycle event listeners
    // These are the ONLY listeners we add here - connection state only
    newSocket.on('connect', () => {
      logger.info('SocketContext: Connected with ID:', newSocket.id);
      setConnected(true);
      setReconnecting(false);
      setError(null);
    });

    newSocket.on('connected', data => {
      logger.debug('SocketContext: Received connection confirmation:', data);
    });

    newSocket.on('connect_error', async error => {
      logger.error('SocketContext: Connection error:', error);
      setConnected(false);
      setError(error.message);

      // If the error is auth-related, refresh the token so the next
      // reconnection attempt uses a valid JWT. Throttled to avoid
      // spamming Auth0 during network issues.
      if (isAuthError(error)) {
        logger.info('SocketContext: Auth-related connect error, refreshing token');
        await refreshSocketToken();
      }
    });

    newSocket.on('disconnect', async reason => {
      logger.info('SocketContext: Disconnected, reason:', reason);
      setConnected(false);

      if (reason === 'io server disconnect') {
        // Server forcefully disconnected us (e.g., auth failure, server restart).
        // Socket.io does NOT auto-reconnect for this case — we must do it manually.
        logger.info('SocketContext: Server-initiated disconnect, refreshing token and reconnecting');
        setReconnecting(true);
        const newToken = await refreshSocketToken();
        if (newToken) {
          newSocket.connect();
        } else {
          // Token refresh failed — try connecting anyway in case server restarted
          // and the existing token is still valid
          newSocket.connect();
        }
      } else {
        // Client-side disconnect (transport close, ping timeout, etc.)
        // Socket.io will auto-reconnect
        setReconnecting(true);
      }
    });

    newSocket.io.on('reconnect_attempt', attemptNumber => {
      logger.info(`SocketContext: Reconnection attempt ${attemptNumber}`);
      setReconnecting(true);
      setReconnectAttempt(attemptNumber);
    });

    newSocket.io.on('reconnect_failed', () => {
      // With Infinity attempts this shouldn't fire, but handle it defensively
      logger.warn('SocketContext: Reconnection failed after max attempts');
      setReconnecting(false);
      setError('Failed to reconnect to server');
    });

    newSocket.io.on('reconnect', attemptNumber => {
      logger.info(`SocketContext: Reconnected after ${attemptNumber} attempts`);
      setConnected(true);
      setReconnecting(false);
      setError(null);
      resetReconnectAttempt();
    });

    newSocket.on('error', error => {
      logger.error('SocketContext: Socket error:', error);
      setError(typeof error === 'string' ? error : 'Socket error occurred');
    });

    // Debug: Log ALL incoming events to trace what's being received
    newSocket.onAny((eventName, ...args) => {
      // Log queue and timer events
      if (eventName.startsWith('queue:') || eventName.startsWith('timer:')) {
        logger.debug(`📨 Socket event received: ${eventName}`, args);
      }
      // Also log room join confirmations
      if (eventName === 'room:joined' || eventName === 'room:left') {
        logger.debug(`🏠 Room event: ${eventName}`, args);
      }
    });

    // Store socket reference and update state to trigger re-render
    socketRef.current = newSocket;
    setSocket(newSocket);

    logger.debug('SOCKET CONNECTED', newSocket.connected, 'for userId=', userId);

    // Cleanup function
    return () => {
      logger.debug('SocketContext: Cleaning up socket connection');
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.close();
        socketRef.current = null;
        setSocket(null);
      }
      setConnected(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshSocketToken is intentionally excluded; it's an unstable closure used inside socket event handlers
  }, [
    isAuthenticated,
    userId,
    role,
    accessToken,
    setConnected,
    setError,
    setReconnecting,
    incrementConnectionAttempts,
    setReconnectAttempt,
    resetReconnectAttempt,
    getNewToken,
  ]);

  // Reconnect on window focus if disconnected
  useEffect(() => {
    if (isE2EAuthMode()) return;

    const handleFocus = () => {
      if (isAuthenticated && userId && !socketRef.current?.connected) {
        logger.debug(
          'SocketContext: Window focused, socket disconnected - triggering reconnect',
        );
        // Refresh token before reconnecting in case it expired while tab was idle
        refreshSocketToken().then(() => {
          socketRef.current?.connect();
        });
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshSocketToken is intentionally excluded; adding it would cause reconnect loops
  }, [isAuthenticated, userId]);

  return (
    <SocketContext.Provider value={{ socket }}>
      {children}
    </SocketContext.Provider>
  );
};

/**
 * Hook to access the socket instance
 *
 * Usage:
 * ```typescript
 * const { socket } = useSocket();
 *
 * useEffect(() => {
 *   if (!socket) return;
 *
 *   socket.on('my:event', handler);
 *   return () => socket.off('my:event', handler);
 * }, [socket, handler]);
 * ```
 */
export const useSocket = () => {
  const context = useContext(SocketContext);

  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }

  return context;
};
